import {useEffect,useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {alpha} from '@mui/material/styles';
import {Alert,Avatar,Badge,Box,Button,Card,CardActionArea,CircularProgress,Link,Stack,Typography} from '@mui/material';
import {useKeycloak} from '@react-keycloak/web';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {useApplication} from './ApplicationContext.js';
import {useNotifications} from './Notification/NotificationsContext';
import {useCurrentUserData,useCurrentProfilePicture} from './CurrentUser.js';
import ConsumerThemeProvider,{CONSUMER_COLORS,LANE4_WEBSITE,POOL_IMAGE,isConsumer} from './ConsumerTheme.js';
import MyAnalysisCard from './AnalysisRequest/Consumer/MyAnalysisCard.js';
import WaterRipples from './WaterRipples.js';
import {getObjectRecords} from '../../core/integration/ObjectManager.js';
import {getCookie} from '../../core/utilities/Utilities.js';
import logoWhite from '../assets/lane4_logo_white.png';
import logoColour from '../assets/lane4_logo_colour.png';

// Lane4 replacement for core Home.js ("Connected" / "Not Connected").
// - Logged out: branded landing over the pool photo, with the login CTA.
// - Consumer: a small dashboard (greeting, latest analysis, shortcuts).
// - Staff: a welcome card that leads to their role's landing page.
// The two login effects below are kept identical to core Home.js
// (post-registration auto-login, redirect to the landing page after Login).

// Logged-out copy: the server locales only load after login, so like core
// Strings.js these live here, keyed by the preferredLanguage cookie.
const OFFLINE_STRINGS={
    en:{
        overline:'Swim analysis',
        title:'See your stroke. Swim faster.',
        body:'Send a freestyle video, get a 10-point technical assessment from your Lane4 coach, and follow your progress from one analysis to the next.',
        login:'Log in',
        sessionExpired:'Your session expired. Please log in again.',
        featureUpload:'Upload your swim',
        featureAssessment:'Coach + AI assessment',
        featureProgress:'Track your progress',
        visit:'lane4.fi',
        tagline:'Never give up!',
    },
    fi:{
        overline:'Uintianalyysi',
        title:'Näe tekniikkasi. Ui nopeammin.',
        body:'Lähetä vapaauintivideo, saat Lane4-valmentajaltasi 10-kohtaisen tekniikka-arvion ja näet kehityksesi analyysista toiseen.',
        login:'Kirjaudu sisään',
        sessionExpired:'Istuntosi vanhentui. Kirjaudu uudelleen.',
        featureUpload:'Lataa uintisi',
        featureAssessment:'Valmentaja + tekoälyarvio',
        featureProgress:'Seuraa kehitystäsi',
        visit:'lane4.fi',
        tagline:'Never give up!',
    },
};
const offlineStrings=()=>{
    const lang=(getCookie('preferredLanguage')||'en').toLowerCase();
    return OFFLINE_STRINGS[lang]||OFFLINE_STRINGS.en;
};

const heroBackground=(strength)=>({
    backgroundImage:[
        `radial-gradient(70% 50% at 85% 0%, ${alpha(CONSUMER_COLORS.cyan,0.22)} 0%, transparent 70%)`,
        `linear-gradient(180deg, ${alpha(CONSUMER_COLORS.navy,strength[0])} 0%, ${alpha(CONSUMER_COLORS.navy,strength[1])} 55%, ${CONSUMER_COLORS.navy} 100%)`,
        `url(${POOL_IMAGE})`,
    ].join(','),
    backgroundSize:'cover',
    backgroundPosition:'center 65%',
    // WaterRipples canvas sits at the bottom; content stays above it
    position:'relative',
    '& > :not(canvas)':{position:'relative',zIndex:1},
});

const FeaturePill=({Icon,label})=>(
    <Stack direction="row" spacing={1} alignItems="center" sx={{
        px:2,py:1.25,borderRadius:100,
        bgcolor:alpha('#ffffff',0.08),
        border:`1px solid ${alpha('#ffffff',0.16)}`,
        backdropFilter:'blur(8px)',
        WebkitBackdropFilter:'blur(8px)',
    }}>
        <Icon sx={{color:CONSUMER_COLORS.cyan,fontSize:20}}/>
        <Typography variant="body2" sx={{color:'#fff',fontWeight:600,whiteSpace:'nowrap'}}>{label}</Typography>
    </Stack>
);

// ======================================================
// LOGGED OUT - LANDING
// ======================================================
const Landing=({onLogin})=>{
    const S=offlineStrings();
    const sessionExpired=(localStorage.getItem('sessionExpiredMessage')||'')!=='';

    return (
        <Box sx={{
            ...heroBackground([0.55,0.82]),
            minHeight:{xs:'calc(100vh - 56px)',sm:'calc(100vh - 64px)'},
            display:'flex',
            flexDirection:'column',
            color:'#fff',
        }}>
            <WaterRipples/>
            <Stack alignItems="center" spacing={3} sx={{flexGrow:1,justifyContent:'center',px:2,py:{xs:5,sm:8},textAlign:'center',maxWidth:640,mx:'auto',width:'100%',boxSizing:'border-box'}}>
                <Box component="img" src={logoWhite} alt="Lane4" sx={{height:{xs:84,sm:112},width:'auto'}}/>
                <Box>
                    <Typography variant="overline" sx={{color:CONSUMER_COLORS.lane4Amber,fontWeight:700,letterSpacing:'0.2em'}}>
                        {S.overline}
                    </Typography>
                    <Typography variant="h3" component="h1" sx={{fontSize:{xs:'1.9rem',sm:'2.8rem'},lineHeight:1.15,mt:0.5}}>
                        {S.title}
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{color:alpha('#ffffff',0.8),maxWidth:520}}>
                    {S.body}
                </Typography>
                {sessionExpired&&
                    <Alert severity="warning" variant="filled" sx={{borderRadius:3,bgcolor:alpha(CONSUMER_COLORS.lane4Amber,0.9),color:CONSUMER_COLORS.navy,'& .MuiAlert-icon':{color:CONSUMER_COLORS.navy}}}>
                        {S.sessionExpired}
                    </Alert>}
                <Button
                    variant="contained"
                    size="large"
                    color="lane4"
                    endIcon={<ArrowForwardRoundedIcon/>}
                    onClick={onLogin}
                    sx={{px:5,py:1.5,fontSize:'1.05rem',boxShadow:`0 10px 30px ${alpha(CONSUMER_COLORS.lane4Amber,0.35)}`}}>
                    {S.login}
                </Button>
                <Stack direction={{xs:'column',sm:'row'}} spacing={1.25} alignItems="center" sx={{pt:2}}>
                    <FeaturePill Icon={VideocamRoundedIcon} label={S.featureUpload}/>
                    <FeaturePill Icon={InsightsRoundedIcon} label={S.featureAssessment}/>
                    <FeaturePill Icon={TrendingUpRoundedIcon} label={S.featureProgress}/>
                </Stack>
            </Stack>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5} sx={{py:2.5,color:alpha('#ffffff',0.65)}}>
                <Typography variant="caption" sx={{fontFamily:'"Days One",sans-serif',textTransform:'uppercase',letterSpacing:'0.12em',color:CONSUMER_COLORS.lane4Amber}}>
                    {S.tagline}
                </Typography>
                <Typography variant="caption">·</Typography>
                <Link href={LANE4_WEBSITE} target="_blank" rel="noopener" underline="hover" sx={{color:'inherit',display:'inline-flex',alignItems:'center',gap:0.5,typography:'caption'}}>
                    {S.visit}<OpenInNewRoundedIcon sx={{fontSize:14}}/>
                </Link>
            </Stack>
        </Box>
    );
};

// ======================================================
// CONSUMER - DASHBOARD
// ======================================================
const ShortcutTile=({Icon,label,onClick,badge=0})=>(
    <Card variant="outlined" sx={{flex:1,minWidth:0}}>
        <CardActionArea onClick={onClick} sx={{p:2,height:'100%'}}>
            <Stack spacing={1}>
                <Badge color="secondary" badgeContent={badge} max={99} sx={{alignSelf:'flex-start'}}>
                    <Box sx={{width:40,height:40,borderRadius:'50%',display:'grid',placeItems:'center',bgcolor:alpha(CONSUMER_COLORS.steelBlue,0.12)}}>
                        <Icon sx={{color:CONSUMER_COLORS.steelBlue}}/>
                    </Box>
                </Badge>
                <Typography variant="subtitle2" noWrap>{label}</Typography>
            </Stack>
        </CardActionArea>
    </Card>
);

const ConsumerHome=({application,userData})=>{
    const navigate=useNavigate();
    const {keycloak}=useKeycloak();
    const {unread}=useNotifications();
    const L=application.getLocale;
    const [latest,setLatest]=useState(null);
    const [loading,setLoading]=useState(true);
    const firstName=keycloak.tokenParsed?.given_name||(userData?.user_name||'').split(' ')[0];
    const profilePicture=useCurrentProfilePicture();

    useEffect(()=>{
        let active=true;
        getObjectRecords({entityType:'AnalysisRequest',entityParams:['_lang='+application.globalData.lang]}).then((data)=>{
            if(!active){return;}
            const records=('error' in data)?[]:(data.records||[]);
            // Server orders created_at desc - the first row is the latest request.
            setLatest(records[0]||null);
            setLoading(false);
        });
        return ()=>{active=false;};
    },[application.globalData.lang]);

    return (
        <Box sx={{p:{xs:2,sm:3},maxWidth:760,mx:'auto'}}>
            <Card sx={{...heroBackground([0.6,0.85]),color:'#fff',borderRadius:5,p:{xs:3,sm:4},mb:3,border:'none'}}>
                <WaterRipples/>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{minWidth:0}}>
                        {/* Own profile picture (set on the Profile page), initial otherwise */}
                        <Avatar
                            src={profilePicture||undefined}
                            alt=""
                            onClick={()=>navigate('/my_user')}
                            sx={{
                                width:{xs:56,sm:64},height:{xs:56,sm:64},flexShrink:0,cursor:'pointer',
                                bgcolor:alpha('#ffffff',0.14),color:'#fff',
                                border:`2px solid ${alpha(CONSUMER_COLORS.cyan,0.7)}`,
                                fontFamily:'"Days One","Montserrat",sans-serif',fontSize:{xs:'1.4rem',sm:'1.6rem'},
                            }}>
                            {(firstName||'?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{minWidth:0}}>
                            <Typography variant="h4" sx={{fontSize:{xs:'1.6rem',sm:'2.1rem'}}}>
                                {L('LabelHomeGreeting').replace('{NAME}',firstName||'')}
                            </Typography>
                            <Typography variant="body1" sx={{color:alpha('#ffffff',0.8),mt:1}}>
                                {L('LabelHomeSubtitle')}
                            </Typography>
                        </Box>
                    </Stack>
                    {/* Hidden on phones to leave the row to the avatar + greeting - the lane4.fi card below carries the logo */}
                    <Box component="img" src={logoWhite} alt="Lane4" sx={{display:{xs:'none',sm:'block'},height:56,width:'auto',flexShrink:0,opacity:0.95}}/>
                </Stack>
                <Button
                    variant="contained"
                    color="lane4"
                    size="large"
                    startIcon={<AddRoundedIcon/>}
                    onClick={()=>navigate('/my_analysis/new')}
                    sx={{mt:3,px:3}}>
                    {L('ButtonNewAnalysisRequest')}
                </Button>
            </Card>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb:1}}>
                <Typography variant="overline" color="text.secondary" sx={{fontWeight:700,letterSpacing:'0.08em'}}>
                    {L('LabelLatestAnalysis')}
                </Typography>
                {latest&&
                    <Button size="small" endIcon={<ArrowForwardRoundedIcon/>} onClick={()=>navigate('/my_analyses')}>
                        {L('ButtonSeeAll')}
                    </Button>}
            </Stack>
            {loading?(
                <Box sx={{display:'flex',justifyContent:'center',py:3}}><CircularProgress size={28}/></Box>
            ):latest?(
                <MyAnalysisCard record={latest} application={application}/>
            ):(
                <Card variant="outlined" sx={{p:3,textAlign:'center'}}>
                    <Typography variant="body2" color="text.secondary">{L('LabelMyAnalysesEmptyState')}</Typography>
                </Card>
            )}

            <Stack direction="row" spacing={1.5} sx={{mt:3}}>
                <ShortcutTile Icon={NotificationsRoundedIcon} label={L('TooltipNotifications')} badge={unread} onClick={()=>navigate('/my_notifications')}/>
                <ShortcutTile Icon={AccountCircleRoundedIcon} label={L('UserMenuProfile')} onClick={()=>navigate('/my_user')}/>
            </Stack>

            <Card variant="outlined" sx={{mt:3}}>
                <CardActionArea component="a" href={LANE4_WEBSITE} target="_blank" rel="noopener" sx={{p:2.5}}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box component="img" src={logoColour} alt="Lane4" sx={{height:44,width:'auto',flexShrink:0}}/>
                        <Box sx={{flexGrow:1,minWidth:0}}>
                            <Typography variant="subtitle1">{L('LabelLane4Website')}</Typography>
                            <Typography variant="body2" color="text.secondary">{L('LabelLane4WebsiteHint')}</Typography>
                        </Box>
                        <OpenInNewRoundedIcon color="action"/>
                    </Stack>
                </CardActionArea>
            </Card>
        </Box>
    );
};

// ======================================================
// STAFF - WELCOME
// ======================================================
const StaffHome=({application,userData,roleConfig})=>{
    const navigate=useNavigate();
    const L=application.getLocale;
    const landing=roleConfig?.landingPage;

    return (
        <Box sx={{p:2,display:'flex',justifyContent:'center'}}>
            <Card variant="outlined" sx={{mt:{xs:3,sm:8},p:{xs:3,sm:5},maxWidth:520,width:'100%',textAlign:'center',borderRadius:4}}>
                <Box component="img" src={logoColour} alt="Lane4" sx={{height:{xs:72,sm:96},width:'auto',mb:2}}/>
                <Typography variant="h5" gutterBottom>
                    {L('LabelHomeGreeting').replace('{NAME}',(userData?.user_name||'').split(' ')[0])}
                </Typography>
                {userData?.role_label&&
                    <Typography variant="body2" color="text.secondary">{userData.role_label}</Typography>}
                {landing&&landing!=='/'&&
                    <Button variant="contained" endIcon={<ArrowForwardRoundedIcon/>} onClick={()=>navigate(landing)} sx={{mt:3}}>
                        {L('ButtonGoToWorkspace')}
                    </Button>}
            </Card>
        </Box>
    );
};

const Home=({roleConfig})=>{
    const {keycloak,initialized}=useKeycloak();
    const isLoggedIn=keycloak.authenticated;
    const navigate=useNavigate();
    const {globalData}=useApplication();
    const userData=useCurrentUserData();
    const application={globalData,getLocale:(key,options)=>globalData?.getLocale(key,options)};

    useEffect(()=>{
        const params=new URLSearchParams(window.location.search);
        const isPostRegister=params.get("postRegister")==="true";
        const loginHint=params.get("loginHint");
        if(isPostRegister&&!isLoggedIn){
            keycloak.login({
                loginHint:loginHint||undefined,
                redirectUri:window.location.origin,
                flow:'standard'
            });
        }
    },[isLoggedIn,keycloak]);

    useEffect(()=>{
        const params=new URLSearchParams(window.location.search);
        const isPostRegister=params.get("postRegister")==="true";
        if(isLoggedIn&&roleConfig&&(localStorage.getItem('handleLoginClicked')==='Y'||isPostRegister)){
            localStorage.setItem('handleLoginClicked','N');
            navigate(roleConfig.landingPage);
        }
    },[isLoggedIn,roleConfig,navigate]);

    if(!initialized) return null;

    const handleLogin=()=>{
        localStorage.setItem('handleLoginClicked','Y');
        keycloak.login();
    };

    if(isLoggedIn===false){
        return (
            <ConsumerThemeProvider force>
                <Landing onLogin={handleLogin}/>
            </ConsumerThemeProvider>
        );
    }
    if(!isLoggedIn||!globalData||!userData?.role_name){
        return null;//Authorization / server variables still loading
    }
    if(isConsumer(userData)){
        return (
            <ConsumerThemeProvider userData={userData}>
                <ConsumerHome application={application} userData={userData}/>
            </ConsumerThemeProvider>
        );
    }
    return <StaffHome application={application} userData={userData} roleConfig={roleConfig}/>;
};

export default Home;
